import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { BaseLineDownload2 } from '@/app/components/icons/BaseLineDownload2';
import { BaseLineShare } from '@/app/components/icons/BaseLineShare';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { BaseVideoPlayer } from '@/app/components/shared/VideoPlayer/BaseVideoPlayer';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent } from '@/app/components/ui/dialog';
import { useCopy } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { cn, formatDate } from '@/app/utils';
import { ArtistPoster, type ArtistPosterRef } from './ArtistPoster';
import { MEDAL_SIZE_CONFIG, MEDAL_VIDEO_BG_URL } from './constants';
import type { Medal as MedalType } from './types';
import { useMedalTracking } from './use-medal-tracking';
import { getMedalConfig, getMedalIcon, hasPosterSupport, isStandardDisplay } from './utils';

interface MedalProps {
  medals?: MedalType[];
  size?: 'small' | 'medium' | 'large' | number;
  className?: string;
  /** 最大展示个数，不传则展示所有 */
  maxCount?: number;
  /** 是否是个人主页,个人主页可以打开勋章详情弹窗 */
  isProfilePage?: boolean;
  /** 是否是我的勋章 */
  isMe?: boolean;
  // 当有值的时候，默认打开勋章详情弹窗
  defaultSelectedMedal?: MedalType;
  onClick?: (medal: MedalType) => void;
  owner?: {
    nickname?: string;
    avatar?: string;
  } | null;
}

export const Medal = ({
  medals = [],
  size = 'medium',
  className,
  maxCount,
  isProfilePage = false,
  isMe = false,
  defaultSelectedMedal,
  owner,
  onClick,
}: MedalProps) => {
  const { copy } = useCopy();
  const t = useTranslations();
  const [selectedMedal, setSelectedMedal] = useState<MedalType | undefined>(defaultSelectedMedal ?? undefined);
  const posterRef = useRef<ArtistPosterRef>(null);

  useEffect(() => {
    setSelectedMedal(defaultSelectedMedal ?? undefined);
  }, [defaultSelectedMedal]);

  const { userInfo } = useStore((state) => ({
    userInfo: state.userInfo,
  }));

  const sizeConfig = useMemo(() => {
    if (typeof size === 'number') {
      return `size-[${size}px]`;
    }
    return MEDAL_SIZE_CONFIG[size];
  }, [size]);

  // 获取当前选中勋章的配置和相关状态
  const { medalConfig, useStandardTheme, canDownloadPoster } = useMemo(() => {
    return {
      medalConfig: getMedalConfig(selectedMedal),
      useStandardTheme: isStandardDisplay(selectedMedal),
      canDownloadPoster: hasPosterSupport(selectedMedal),
    };
  }, [selectedMedal]);

  const displayMedals = useMemo(() => {
    const validMedals = medals.sort((a, b) => {
      return (b?.rank ?? 0) - (a?.rank ?? 0);
    });

    return maxCount ? validMedals.slice(0, maxCount) : validMedals;
  }, [medals, maxCount]);

  // 勋章埋点上报
  const { reportMedalClick, setMedalElementRef } = useMedalTracking(displayMedals);

  const handleDownloadPoster = () => {
    if (posterRef.current) {
      posterRef.current.download();
    }
    reportMedalClick(selectedMedal ?? {}, 'download');
  };

  if (displayMedals.length === 0) {
    return null;
  }

  return (
    <>
      <div className={cn('flex items-center gap-1', className)}>
        {displayMedals.map((medal, index) => {
          const icon = getMedalIcon(medal);
          if (!icon) return null;

          return (
            <div
              key={`medal-${medal.user_medal_type}`}
              ref={(el) => setMedalElementRef(el, index)}
              data-index={index}
              className="inline-block"
            >
              <img
                src={icon}
                alt={medal.medal_name}
                className={cn(sizeConfig, 'cursor-pointer')}
                onClick={(e) => {
                  e.stopPropagation();
                  onClick?.(medal);
                  reportMedalClick(medal, 'check');
                  if (isProfilePage) {
                    setSelectedMedal(medal);
                  }
                }}
              />
            </div>
          );
        })}
      </div>

      <Dialog
        open={!!selectedMedal}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedMedal(undefined);
            reportMedalClick(selectedMedal ?? {}, 'cancel');
          }
        }}
      >
        {useStandardTheme ? (
          <DialogContent
            className="h-[520px] max-w-[390px] rounded-[20px] border border-system-white32 bg-black p-0"
            onClick={(e) => e.stopPropagation()}
            overlayClassname="bg-black/40 backdrop-blur-[20px]"
          >
            <div className="flex h-full flex-col items-center justify-center bg-center bg-cover pb-[45px]">
              <div
                className="h-[241px] w-[237px] bg-cover bg-no-repeat"
                style={{
                  backgroundImage: `url(${MEDAL_VIDEO_BG_URL})`,
                }}
              >
                {medalConfig?.video && (
                  <BaseVideoPlayer
                    src={medalConfig.video}
                    poster={medalConfig.poster}
                    autoPlay
                    hideControls
                    hasAudio={false}
                    defaultVolume={0}
                    className="mx-auto mt-10 h-[165px] w-[128px] object-cover drop-shadow-[0_4px_4px_rgba(0,0,0,0.25)]"
                  />
                )}
              </div>
              {owner && (
                <div className="mt-6 flex items-center text-xs leading-[1.4]">
                  <img src={owner?.avatar || ''} alt="avatar" className="mr-1 size-5 rounded-full object-cover" />
                  <CustomTooltip content={owner?.nickname} showOnlyOnOverflow maxLines={1}>
                    <div className="max-w-16">{owner?.nickname}</div>
                  </CustomTooltip>
                </div>
              )}
              <div
                className={cn(
                  'mx-1 mt-2 bg-clip-text font-semibold text-[22px] text-transparent leading-[1.4]',
                  medalConfig?.nameClassName || '',
                )}
              >
                {selectedMedal?.medal_name}
              </div>

              <div className="mx-8 mt-1 text-center text-system-white48 text-xs leading-[1.4]">
                {selectedMedal?.medal_achievement_condition}
              </div>
              <div className="flex items-center gap-4">
                <Button
                  className={cn(
                    'mt-[28px] hidden h-8 min-w-[100px] items-center rounded-full bg-system-white12 px-6',
                    isMe && 'inline-flex',
                  )}
                  onClick={() => {
                    copy(
                      `${window.location.origin}/profile/${userInfo?.id}?user_medal_type=${selectedMedal?.user_medal_type}`,
                      {
                        copySuccessMessage: t('create.share.description'),
                      },
                    );
                    reportMedalClick(selectedMedal ?? {}, 'share');
                  }}
                >
                  <BaseLineShare className="mr-1 size-3.5" />
                  {t('task.action.share')}
                </Button>
                <Button
                  className={cn(
                    'mt-[28px] hidden h-8 min-w-[100px] items-center rounded-full bg-system-white12 px-6',
                    canDownloadPoster && isMe && 'inline-flex',
                  )}
                  onClick={handleDownloadPoster}
                >
                  <BaseLineDownload2 className="mr-1 size-3.5" />
                  {t('creation.action.download')}
                </Button>

                {/* 隐藏的海报组件，用于生成下载图片 */}
                {canDownloadPoster && selectedMedal && (
                  <div className="-left-[9999px] pointer-events-none fixed top-0 opacity-0">
                    <ArtistPoster ref={posterRef} medal={selectedMedal} owner={owner} />
                  </div>
                )}
              </div>
            </div>
            <div className="before:-translate-y-1/2 after:-translate-y-1/2 -translate-x-1/2 absolute bottom-5 left-1/2 flex items-center justify-center before:absolute before:top-1/2 before:left-0 before:h-[1px] before:w-6 before:bg-system-white24 after:absolute after:top-1/2 after:right-0 after:h-[1px] after:w-6 after:bg-system-white24">
              <div className="mx-6 px-4 text-system-white/40 text-xs leading-[1.4]">
                {formatDate(selectedMedal?.awarded_at ?? '')}
              </div>
            </div>
          </DialogContent>
        ) : (
          <DialogContent
            className="h-[590px] max-w-[490px] rounded-12 bg-gradient-to-b from-[#07284D] to-[#0F1427] p-0"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex h-full flex-col items-center justify-center bg-center bg-cover pb-[45px]"
              style={{
                backgroundImage: `url('https://image01-aliyun.vidu.zone/vidu/media-asset/medal_lighting-453e6809.webp')`,
              }}
            >
              <img
                src={getMedalIcon(selectedMedal) ?? ''}
                alt={selectedMedal?.medal_name}
                className="mt-[98px] size-[220px] drop-shadow-[0_10px_40px_rgba(4,10,26,0.5)]"
              />

              <div className="relative mt-[46px] flex items-center font-normal text-[#FFE3BB] text-[36px] before:absolute before:right-full before:h-[1px] before:w-full before:max-w-[70px] before:bg-gradient-to-r before:from-transparent before:to-[#FFE3BB] before:opacity-40 after:absolute after:left-full after:h-[1px] after:w-full after:max-w-[70px] after:bg-gradient-to-r after:from-[#FFE3BB] after:to-transparent after:opacity-40">
                <span className="mx-2 flex-shrink-0">{selectedMedal?.medal_name}</span>
              </div>

              <div className="mt-6 font-normal text-[#FFF1DD] text-base">
                {selectedMedal?.medal_achievement_condition}
              </div>
              <div className="mt-4 font-normal text-[rgba(255,241,221,0.64)] text-sm">
                <span>获得时间：</span>
                {formatDate(selectedMedal?.awarded_at ?? '')}
              </div>
              <Button
                className={cn('mt-4 hidden h-[32px] min-w-[68px] rounded-6', isMe && 'inline-flex')}
                variant="primary"
                onClick={() =>
                  copy(
                    `${window.location.origin}/profile/${userInfo?.id}?user_medal_type=${selectedMedal?.user_medal_type}`,
                    {
                      copySuccessMessage: t('create.share.description'),
                    },
                  )
                }
              >
                {t('task.action.share')}
              </Button>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </>
  );
};
